import React, { memo, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createUseStyles, Text, Icon, Loader, Modal } from '@Sage/hoi-poi-ui';
import WidgetLayout from '../WidgetLayout';
import {
    getChartDynamicConfig,
    getMapDynamicConfig,
    getKpiDynamicConfig,
    getTableDynamicConfig,
    createWidget,
    deleteWidget,
} from 'lib/services/forceBI';
import { useWeb5Context } from 'lib/Web5Context';
import { downloadSnapshot } from '../../utils/downloadSnapshots';
import { downloadTable, downloadMultipleTables } from '../../utils/downloadTables';
import { CHARTS_LIST, NOT_CHART_TYPES, FORCE_BI_SUBMODULE } from '../../constants';
import Footer from './Footer';
import Header from './Header';
import Charts from '../Charts';
import Empty from './Empty';
import Expanded from './Expanded';
import { successToast, errorToast } from 'utils/toast';
import { subscribe } from 'lib/EventBuser';
import { REFRESH_FORCE_BI_VISUALS } from 'lib/events';
import { isEmptyObject } from 'utils/objects';

import styles from './styles';

const useStyles = createUseStyles(styles, 'Widget');

const Widget = memo(
    ({
        data,
        onEdit,
        onSelect,
        isSelected,
        googleMapsApiKey,
        getSnapshot,
        getSingleSnapshot,
        canUseEmail,
        showEmailEditor,
        dashboardFilters,
        forceBIUtils,
        getRef,
        isMobile,
        afterDeleteWidget,
        afterDuplicateWidget,
        calculateNewWidgetPosition,
        canCreate,
        canEdit,
        canDelete,
        canShare,
        customDashboardFilters,
        updateErrorWidgets,
    }) => {
        const classes = useStyles();
        const { t, logEvent, entities } = useWeb5Context();

        const [isLoading, setIsLoading] = useState(true);
        const [isLoaded, setIsLoaded] = useState(false);
        const [isExpanded, setIsExpanded] = useState(false);
        const [isDeleteOpen, setIsDeleteOpen] = useState(false);
        const [isLoadingDelete, setIsLoadingDelete] = useState(false);
        const [isLoadingDuplicate, setIsLoadingDuplicate] = useState(false);
        const [config, setConfig] = useState(null);
        const [isError, setIsError] = useState(false);
        const [canExpand, setCanExpand] = useState(true);
        const [wrongFiltersMessage, setWrongFiltersMessage] = useState('');
        const imageRef = useRef(null);

        const isFirstRender = useRef(true);

        const isChart = useMemo(() => {
            if (CHARTS_LIST.includes(data?.chartType)) return true;
            return false;
        }, [data]);

        const isMap = useMemo(() => {
            if (data?.chartType?.toLowerCase() === NOT_CHART_TYPES.map) return true;
            return false;
        }, [data]);

        const isKpi = useMemo(() => {
            if (data?.chartType?.toLowerCase() === NOT_CHART_TYPES.kpi) return true;
            return false;
        }, [data]);

        const isTable = useMemo(() => {
            if (data?.chartType?.toLowerCase() === NOT_CHART_TYPES.table) return true;
            return false;
        }, [data]);

        const completeLoad = useCallback(() => {
            setIsLoading(false);
            setIsLoaded(true);
        }, []);

        const getWrongFiltersMessage = useCallback(
            (error) => {
                const { getFiltersErrorMessage } = forceBIUtils;
                const errorObj = error.response?.data || null;
                if (errorObj?.error && errorObj?.fields && Object.keys(errorObj?.fields).length) {
                    const message = getFiltersErrorMessage({
                        message: errorObj.error,
                        entityFields: errorObj.fields,
                    });
                    setWrongFiltersMessage(message);
                }
            },
            [forceBIUtils],
        );

        const getIsWidgetWithoutValues = useCallback(
            (incomingConfig) => {
                const innerConfig = incomingConfig || config;
                let hasNoValues = false;
                switch (data.chartType) {
                    case NOT_CHART_TYPES.map:
                        if (!innerConfig?.length) hasNoValues = true;
                        break;
                    case NOT_CHART_TYPES.kpi:
                    case NOT_CHART_TYPES.table:
                        if (!innerConfig?.data?.length) hasNoValues = true;
                        break;
                    default:
                        if (!innerConfig?.datasets?.length) hasNoValues = true;
                }
                return hasNoValues;
            },
            [config, data.chartType],
        );

        const updateVisuals = useCallback(
            ({ data, filtersObj, customDashboardFilters }) => {
                const { processDataToSendFromWidget } = forceBIUtils;
                const finalData = processDataToSendFromWidget({
                    data,
                    dashboardFilters: filtersObj || dashboardFilters,
                    customFilters: customDashboardFilters,
                });

                setIsError(false);
                setCanExpand(false);
                setWrongFiltersMessage('');
                setIsLoading(true);

                let promise = null;

                if (isMap) promise = getMapDynamicConfig(finalData);
                else if (isKpi) promise = getKpiDynamicConfig(finalData);
                else if (isTable) promise = getTableDynamicConfig(finalData);
                else promise = getChartDynamicConfig(finalData);

                if (!promise) return Promise.resolve();

                return promise
                    .then((response) => {
                        if (!response?.data) {
                            setIsError(true);
                            setCanExpand(false);
                            setConfig(null);
                            updateErrorWidgets.add(data.id);
                            return;
                        }
                        setConfig(response.data);
                        const hasNoValues = getIsWidgetWithoutValues(response.data);
                        if (hasNoValues) {
                            setCanExpand(false);
                            updateErrorWidgets.add(data.id);
                        } else {
                            setCanExpand(true);
                            updateErrorWidgets.remove(data.id);
                        }
                        return {
                            widget: finalData,
                            config: response.data,
                        };
                    })
                    .catch((error) => {
                        getWrongFiltersMessage(error);
                        setConfig(null);
                        setIsError(true);
                        setCanExpand(false);
                        updateErrorWidgets.add(data.id);
                    })
                    .finally(completeLoad);
            },
            [
                isKpi,
                isMap,
                isTable,
                dashboardFilters,
                forceBIUtils,
                completeLoad,
                getWrongFiltersMessage,
                updateErrorWidgets,
                getIsWidgetWithoutValues,
            ],
        );

        useEffect(() => {
            return subscribe(
                REFRESH_FORCE_BI_VISUALS,
                ({ id, data: newData, onSuccess, customDashboardFilters, ...dashboardFilters }) => {
                    if (id && parseInt(id, 10) === parseInt(data.id, 10)) {
                        let obj = { data };
                        if (newData) obj.data = newData;
                        if (!isEmptyObject(dashboardFilters)) obj.filtersObj = dashboardFilters;
                        updateVisuals({ ...obj, customDashboardFilters }).then((data) => {
                            onSuccess && onSuccess(data);
                        });
                    }
                },
            );
        }, [updateVisuals, data]);

        useEffect(() => {
            // load for mobile versión, will provably need the query to obtain the widget data
            if (isFirstRender.current && isMobile) {
                updateVisuals({ data, customDashboardFilters });
            }
        }, [data, isMobile, updateVisuals, customDashboardFilters]);

        const getImage = useCallback(
            (image, isExpanded) => {
                if (!image) return;
                imageRef.current = image;
                getSnapshot({ id: data.id, title: data?.title || '', image });
            },
            [data?.id, data?.title, getSnapshot],
        );

        const handleOnEdit = useCallback(() => {
            onEdit && onEdit(data);
        }, [onEdit, data]);

        const onOpenEditFromExpand = useCallback(() => {
            if (!canEdit) return;
            setIsExpanded(false);
            handleOnEdit();
        }, [handleOnEdit, canEdit]);

        const onDuplicate = useCallback(() => {
            setIsLoadingDuplicate(true);
            const { processDataToSendFromCrud } = forceBIUtils;

            let finalData = processDataToSendFromCrud({
                data,
            });

            finalData.title = `${finalData.title} copy`;
            delete finalData.id;

            if (!calculateNewWidgetPosition) {
                console.error('CalculateNewWidgetPosition is necessary to duplicate the widget');
                return;
            }

            const newPosition = calculateNewWidgetPosition();
            const newData = {
                ...finalData,
                position: newPosition,
            };

            createWidget(newData)
                .then((response) => {
                    logEvent({
                        event: entities.ANALYTICS_REPORT.trueName,
                        functionality: 'createWidget',
                        submodule: FORCE_BI_SUBMODULE,
                    });
                    successToast({ t, text: t('label_duplicate_success') });
                    afterDuplicateWidget && afterDuplicateWidget(response.data);
                })
                .catch(() => errorToast({ t, text: t('label_error_duplicate') }))
                .finally(() => {
                    setIsLoadingDuplicate(false);
                });
        }, [
            data,
            forceBIUtils,
            calculateNewWidgetPosition,
            t,
            afterDuplicateWidget,
            logEvent,
            entities,
        ]);

        const onConfirmDelete = useCallback(() => {
            setIsLoadingDelete(true);
            deleteWidget(data.id)
                .then(() => {
                    setIsDeleteOpen(false);
                    afterDeleteWidget && afterDeleteWidget([data.id]);
                })
                .catch(() => {
                    errorToast({ t, text: t('label_error_delete') });
                })
                .finally(() => {
                    setIsLoadingDelete(false);
                });
        }, [data.id, afterDeleteWidget, t]);

        const onCloseDelete = useCallback(() => {
            setIsDeleteOpen(false);
        }, []);

        const handleOnExpand = useCallback(() => {
            setIsExpanded(true);
            logEvent({
                event: entities.ANALYTICS_REPORT.trueName,
                functionality: 'expand',
                submodule: FORCE_BI_SUBMODULE,
            });
        }, [logEvent, entities]);

        const onCloseExpand = useCallback(() => {
            setIsExpanded(false);
        }, []);

        const stopPropagation = useCallback((e) => {
            e?.stopPropagation?.();
        }, []);

        const onDownloadTable = useCallback(() => {
            if (isLoading) return;
            downloadTable({
                title: data.title,
                data: config,
                t,
            });
            logEvent({
                event: entities.ANALYTICS_REPORT.trueName,
                functionality: 'exportToExcel',
                submodule: FORCE_BI_SUBMODULE,
            });
        }, [isLoading, t, data, config, logEvent, entities]);

        const onDownloadOtherTable = useCallback(() => {
            logEvent({
                event: entities.ANALYTICS_REPORT.trueName,
                functionality: 'exportToExcel',
                submodule: FORCE_BI_SUBMODULE,
            });
            downloadMultipleTables({
                widgets: [data],
                dashboardFilters,
                forceBIUtils,
                t,
            });
        }, [logEvent, entities, t, data, dashboardFilters, forceBIUtils]);

        const onDownloadSnapshot = useCallback(() => {
            if (!imageRef.current) return;
            downloadSnapshot({
                id: data.id,
                title: data?.title || '',
                image: imageRef.current,
                withTitle: true,
            });
            logEvent({
                event: entities.ANALYTICS_REPORT.trueName,
                functionality: 'exportToImage',
                submodule: FORCE_BI_SUBMODULE,
            });
            successToast({ t, text: t('label_image_downloaded') });
        }, [data?.id, data?.title, t, logEvent, entities]);

        const actions = useMemo(() => {
            let finalActions = [];

            if (canExpand) {
                finalActions.push({
                    title: t('action_extend'),
                    iconName: 'fullscreenArrows',
                    onClick: handleOnExpand,
                });
            }

            if (canEdit) {
                finalActions.push({
                    title: t('label_edit'),
                    iconName: 'edit',
                    onClick: handleOnEdit,
                });
            }

            return finalActions;
        }, [handleOnEdit, handleOnExpand, canEdit, t, canExpand]);

        const handleShowEmailEditor = useCallback(() => {
            const attachment = getSingleSnapshot(data.id);
            let attachments = [];
            if (attachment) attachments = [attachment];
            showEmailEditor({ attachments });
            logEvent({
                event: entities.ANALYTICS_REPORT.trueName,
                functionality: 'shareEmail',
                submodule: FORCE_BI_SUBMODULE,
            });
        }, [data?.id, getSingleSnapshot, showEmailEditor, logEvent, entities]);

        const hideDownload = useMemo(() => {
            if (isError) {
                return true;
            }
            let hide = false;
            hide = getIsWidgetWithoutValues();
            return hide;
        }, [isError, getIsWidgetWithoutValues]);

        const menuItems = useMemo(() => {
            let menus = [];

            if (canCreate) {
                menus.push({
                    title: <Text>{t('action_duplicate')}</Text>,
                    icon: <Icon name="duplicate" />,
                    onClick: onDuplicate,
                });
                menus.push({ divider: true });
            }

            if (!hideDownload) {
                if (isChart) {
                    menus.push({
                        title: <Text>{t('label_download_as_image')}</Text>,
                        icon: <Icon name="image" />,
                        onClick: onDownloadSnapshot,
                    });
                }

                if (isKpi || isMap || isChart) {
                    menus.push({
                        title: <Text>{t('label_download_as_excel')}</Text>,
                        icon: <Icon className={classes.excelIcon} name="xls" />,
                        onClick: onDownloadOtherTable,
                    });
                }

                if (isTable) {
                    menus.push({
                        title: <Text>{t('label_download_as_excel')}</Text>,
                        icon: <Icon className={classes.excelIcon} name="xls" />,
                        onClick: onDownloadTable,
                    });
                }

                if (canShare && canUseEmail) {
                    menus.push({
                        title: <Text>{t('action_sharebymail')}</Text>,
                        icon: <Icon name="email" />,
                        onClick: handleShowEmailEditor,
                    });
                }
            }

            if (canDelete) {
                if (menus.length > 2) menus.push({ divider: true });
                menus.push({
                    title: <Text>{t('action_delete')}</Text>,
                    icon: <Icon name="delete" />,
                    onClick: () => {
                        setIsDeleteOpen(true);
                    },
                });
            }

            return menus;
        }, [
            isChart,
            isTable,
            isKpi,
            isMap,
            t,
            canUseEmail,
            onDownloadSnapshot,
            onDownloadTable,
            onDownloadOtherTable,
            onDuplicate,
            canCreate,
            canDelete,
            canShare,
            handleShowEmailEditor,
            classes.excelIcon,
            hideDownload,
        ]);

        const handleOnSelect = useCallback(() => {
            onSelect && onSelect(data.id);
        }, [onSelect, data?.id]);

        return (
            <WidgetLayout
                getRef={getRef}
                id={data.id}
                isSelected={isSelected}
                header={
                    <Header
                        title={data.title}
                        onSelect={handleOnSelect}
                        isSelected={isSelected}
                        actions={actions}
                        menuItems={menuItems}
                        isLoaded={isLoaded}
                        isLoadingDuplicate={isLoadingDuplicate}
                    />
                }
                footer={
                    <Footer
                        widgetFilters={{
                            filters: data.filters,
                            crossFilters: data.crossFilters,
                            operators: data.operators,
                            crossOperators: data.crossOperators,
                        }}
                        dashboardFilters={dashboardFilters}
                        forceBIUtils={forceBIUtils}
                        customDashboardFilters={customDashboardFilters}
                    />
                }
            >
                {isLoading && (
                    <div className={classes.loader}>
                        <Loader size="big" />
                    </div>
                )}
                {!isLoading && !isError && (
                    <div className={classes.content} onClick={stopPropagation}>
                        <Charts
                            chartType={data.chartType}
                            config={config}
                            getImage={getImage}
                            googleMapsApiKey={googleMapsApiKey}
                        />
                    </div>
                )}
                {isLoaded && !isLoading && isError && (
                    <>
                        {wrongFiltersMessage && (
                            <div className={classes.empty}>
                                <Empty title={wrongFiltersMessage} />
                            </div>
                        )}
                        {!wrongFiltersMessage && (
                            <div className={classes.empty}>
                                <Empty
                                    type="error"
                                    onRefresh={() =>
                                        updateVisuals({ data, customDashboardFilters })
                                    }
                                />
                            </div>
                        )}
                    </>
                )}
                <Expanded
                    isOpen={isExpanded}
                    canEdit={canEdit}
                    onEdit={onOpenEditFromExpand}
                    data={data}
                    config={config}
                    onClose={onCloseExpand}
                    dashboardFilters={dashboardFilters}
                    forceBIUtils={forceBIUtils}
                    googleMapsApiKey={googleMapsApiKey}
                    getSnapshot={getSnapshot}
                />
                <Modal
                    title={t('action_delete', [data?.title || ''])}
                    size="small"
                    isOpen={isDeleteOpen}
                    onRequestClose={onCloseDelete}
                    onConfirm={onConfirmDelete}
                    onCancel={onCloseDelete}
                    confirmText={t('action_delete')}
                    cancelText={t('action_cancel')}
                    isConfirmLoading={isLoadingDelete}
                >
                    {<Text>{t('confirm_delete_name_variable', [`"${data?.title || ''}"`])}</Text>}
                </Modal>
            </WidgetLayout>
        );
    },
);

export default Widget;
